import React, { useMemo } from 'react';
import { Users } from 'lucide-react';
import { FilterBar } from './FilterBar';
import { EmployeeList } from './EmployeeList';
import type { UserActivityItem, TrackingFilters, StatusFilter, LocationStatusFilter } from '../../types/tracking';

interface EmployeePanelProps {
  filters: TrackingFilters;
  activities: UserActivityItem[];
  loading: boolean;
  error: string | null;
  selectedIds: Set<string>;
  onSelect: (userId: string, multiSelect: boolean) => void;
  onDateChange: (date: string) => void;
  onToggleProgram: (program: string) => void;
  onStatusChange: (status: StatusFilter) => void;
  onLocationStatusChange: (status: LocationStatusFilter) => void;
  onSearchChange: (query: string) => void;
  onRetry: () => void;
}

export const EmployeePanel: React.FC<EmployeePanelProps> = ({
  filters,
  activities,
  loading,
  error,
  selectedIds,
  onSelect,
  onDateChange,
  onToggleProgram,
  onStatusChange,
  onLocationStatusChange,
  onSearchChange,
  onRetry,
}) => {
  // Apply client-side filters: status + search
  const filteredEmployees = useMemo(() => {
    return activities.filter(emp => {
      // Status filter
      if (filters.status === 'live' && !emp.is_live) return false;
      if (filters.status === 'offline' && emp.is_live) return false;

      // Search filter — name, role, OR RGF employee id (so reviewers can
      // jump straight to a specific changemaker by id when names collide).
      if (filters.searchQuery) {
        const q = filters.searchQuery.toLowerCase();
        const matchesName = emp.name.toLowerCase().includes(q);
        const matchesRole = emp.role.some(r => r.toLowerCase().includes(q));
        const matchesEmployeeId = (emp.employee_id ?? '').toLowerCase().includes(q);
        if (!matchesName && !matchesRole && !matchesEmployeeId) return false;
      }

      return true;
    });
  }, [activities, filters.status, filters.searchQuery]);

  const liveCount = activities.filter(e => e.is_live).length;

  return (
    <div className="flex flex-col h-full bg-white border-l border-rgf-gray-200">
      {/* Header */}
      <div className="px-4 py-3 border-b border-rgf-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Users className="h-5 w-5 text-rgf-primary-600" />
            <h2 className="text-sm font-semibold text-rgf-gray-900">Users</h2>
          </div>
          <div className="flex items-center gap-2 text-xs text-rgf-gray-500">
            <span className="inline-flex items-center gap-1">
              <span className="h-2 w-2 rounded-full bg-green-500 animate-pulse" />
              {liveCount} live
            </span>
            <span>/ {activities.length} total</span>
          </div>
        </div>
      </div>

      {/* Filters */}
      <FilterBar
        filters={filters}
        onDateChange={onDateChange}
        onToggleProgram={onToggleProgram}
        onStatusChange={onStatusChange}
        onLocationStatusChange={onLocationStatusChange}
        onSearchChange={onSearchChange}
      />

      {/* Error banner */}
      {error && (
        <div className="mx-4 mt-2 p-2 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700 flex items-center justify-between">
          <span>{error}</span>
          <button
            onClick={onRetry}
            className="ml-2 underline hover:text-red-900"
          >
            Retry
          </button>
        </div>
      )}

      {/* Loading skeleton */}
      {loading ? (
        <div className="flex-1 p-4 space-y-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="flex items-center gap-3 animate-pulse">
              <div className="h-10 w-10 rounded-full bg-rgf-gray-200" />
              <div className="flex-1 space-y-2">
                <div className="h-3 bg-rgf-gray-200 rounded w-2/3" />
                <div className="h-2.5 bg-rgf-gray-200 rounded w-1/2" />
              </div>
            </div>
          ))}
        </div>
      ) : (
        <EmployeeList
          employees={filteredEmployees}
          selectedIds={selectedIds}
          onSelect={onSelect}
        />
      )}
    </div>
  );
};
